import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Grid,
  TableContainer,
  Container,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import WinnerAndLoserTable from './table';

const AntTabs = styled(Tabs)(() => ({
  border: '1px solid #e8e8e8',
  borderBottom: 'transparent',
  borderRadius: '8px 8px 0 0',
  background: '#fbfbfc',
  '& .MuiTabs-indicator': {
    backgroundColor: '#3a3a6f',
  },
  '& .MuiTabs-scrollButtons': {
    '&.Mui-disabled': { opacity: 0.3 },
  },
}));

const AntTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.85)',
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    color: '#161b57',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 0
      : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgb(19, 47, 76)'
      : '1px solid #eae9ee',
  borderRadius: '7px',
  [theme.breakpoints.down('md')]: {
    borderRadius: '0 0 7px 7px',
  },
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(1.5, 2, 1.5, 2),
    borderRadius: '7px 7px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 600,
      color: '#13113c',
      fontSize: '.875rem',
    },
  },
  '& .Item-Body': {
    padding: theme.spacing(1),
  },
}));

const hasData = (data) => data != null && data !== '';

const WinnerLoserCard = ({ tab, isMobile }) => (
  <Item
    component="div"
    className="py-2"
    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
  >
    <TableContainer sx={{ borderRadius: '7px' }}>
      <WinnerAndLoserTable
        data={tab.data}
        tableName={tab.tableName}
        label={tab.label}
        value={tab.value}
        isPct={tab.isPct}
        color={tab.color}
        isMobile={isMobile}
      />
    </TableContainer>
  </Item>
);

const WinnerAndLoser = ({ fund_metrics }) => {
  const {
    profit5: top5Win,
    pctWin5: top5WinPct,
    loss5: top5Loss,
    pctLoss5: top5LossPct,
  } = fund_metrics || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = React.useCallback((event, newIndex) => {
    setTabIndex(newIndex);
  }, []);

  const visibleTabs = React.useMemo(
    () =>
      [
        { key: 'winPct', data: top5WinPct, tableName: 'Top 5 Winners (%)', label: 'name', value: 'pctp', isPct: true, color: '#0E8614' },
        { key: 'winAmt', data: top5Win, tableName: 'Top 5 Winners ($)', label: 'name', value: 'profit', isPct: false, color: '#0E8614' },
        { key: 'lossPct', data: top5LossPct, tableName: 'Top 5 Losers (%)', label: 'name', value: 'pctp', isPct: true, color: '#EB0000' },
        { key: 'lossAmt', data: top5Loss, tableName: 'Top 5 Losers ($)', label: 'name', value: 'profit', isPct: false, color: '#EB0000' },
      ].filter((tab) => hasData(tab.data)),
    [top5WinPct, top5Win, top5LossPct, top5Loss],
  );

  React.useEffect(() => {
    if (tabIndex > visibleTabs.length - 1) {
      setTabIndex(0);
    }
  }, [tabIndex, visibleTabs.length]);

  if (visibleTabs.length === 0) {
    return null;
  }

  const safeTabIndex = Math.min(tabIndex, visibleTabs.length - 1);

  return (
    <Container maxWidth="xl">
      <Box
        component="div"
        // className="row"
        sx={{
          mt: 1.5,
          position: 'relative',
          '& .MuiTabs-root .MuiTabs-scrollButtons': {
            position: 'absolute',
            py: 0,
            opacity: 1,
            zIndex: 99,
            height: '48px',
          },
          '& .MuiTabs-root .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
          '& .MuiTabs-root .MuiTabScrollButton-root:first-of-type': {
            left: 1,
            pl: '5px',
            background: 'rgb(251 251 252)',
            justifyContent: 'flex-start',
            width: '37px',
            borderRadius: '7px 0 0 0',
          },
          '& .MuiTabs-root .MuiTabScrollButton-root:last-of-type': {
            right: 1,
            pr: '5px',
            background: 'rgb(251 251 252)',
            justifyContent: 'flex-end',
            width: '37px',
            borderRadius: '0 7px 0 0',
          },
        }}
      >
        {isMobile ? (
          <>
            <AntTabs
              value={safeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              aria-label="Winners and losers categories"
            >
              {visibleTabs.map((tab) => (
                <AntTab
                  key={tab.key}
                  label={tab.tableName}
                  id={`wl-tab-${tab.key}`}
                  aria-controls={`wl-tabpanel-${tab.key}`}
                />
              ))}
            </AntTabs>
            {visibleTabs.map((tab, index) => (
              <Box
                key={tab.key}
                role="tabpanel"
                hidden={safeTabIndex !== index}
                id={`wl-tabpanel-${tab.key}`}
                aria-labelledby={`wl-tab-${tab.key}`}
              >
                {safeTabIndex === index && (
                  <WinnerLoserCard tab={tab} isMobile={isMobile} />
                )}
              </Box>
            ))}
          </>
        ) : (
          <Grid container spacing={1.2}>
            {visibleTabs.map((tab) => (
              <Grid key={tab.key} size={{ xs: 12, md: 3 }}>
                <WinnerLoserCard tab={tab} isMobile={isMobile} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default WinnerAndLoser;
